/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";

export interface SNMPDevice {
  id: number;
  name: string;
  host: string;
  port: number;
  community?: string;
  version: "1" | "2c";
  pollIntervalSeconds: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SNMPDeviceStatus {
  reachable: boolean;
  sysDescr?: string;
  sysUpTimeTicks?: number;
  interfaceCount?: number;
  scrapeDurationMs?: number;
  polledAt?: string;
  error?: string;
}

export interface SNMPInterfaceStats {
  id: number;
  interfaceId: number;
  adminStatus: number; // 1=up, 2=down, 3=testing
  operStatus: number; // 1=up, 2=down, 3=testing, ...
  inOctets: number;
  outOctets: number;
  inErrors: number;
  outErrors: number;
  inDiscards: number;
  outDiscards: number;
  createdAt: string;
}

export interface SNMPInterface {
  id: number;
  deviceId: number;
  ifIndex: number;
  ifDescr: string;
  ifAlias?: string;
  ifType: number;
  ifSpeed: number;
  updatedAt: string;
  latest?: SNMPInterfaceStats;
}

export interface SNMPMacEntry {
  deviceId: number;
  ifIndex: number;
  macAddress: string;
  updatedAt: string;
}

export interface PaginatedSNMPInterfaceStats {
  data: SNMPInterfaceStats[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateSNMPDeviceRequest {
  name: string;
  host: string;
  port?: number;
  community: string;
  version: "1" | "2c";
  pollIntervalSeconds?: number;
  enabled: boolean;
}

export interface UpdateSNMPDeviceRequest extends CreateSNMPDeviceRequest {
  id: number;
}

async function parseErrorOr(response: Response, fallback: string): Promise<never> {
  const errorData = await response.json().catch(() => ({}));
  throw new Error(errorData.error || fallback);
}

// Device management
export async function getSNMPDevices(): Promise<SNMPDevice[]> {
  const response = await fetch(getApiUrl("/snmp/devices"));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch devices");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function getSNMPDevice(id: number): Promise<SNMPDevice> {
  const response = await fetch(getApiUrl(`/snmp/devices/${id}`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch device");
  return response.json();
}

export async function createSNMPDevice(
  data: CreateSNMPDeviceRequest,
): Promise<SNMPDevice> {
  const response = await fetch(getApiUrl("/snmp/devices"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) return parseErrorOr(response, "Failed to create device");
  return response.json();
}

export async function updateSNMPDevice(
  id: number,
  data: UpdateSNMPDeviceRequest,
): Promise<SNMPDevice> {
  const response = await fetch(getApiUrl(`/snmp/devices/${id}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) return parseErrorOr(response, "Failed to update device");
  return response.json();
}

export async function deleteSNMPDevice(id: number): Promise<void> {
  const response = await fetch(getApiUrl(`/snmp/devices/${id}`), {
    method: "DELETE",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to delete device");
}

// Device monitoring
export async function getSNMPDeviceStatus(id: number): Promise<SNMPDeviceStatus> {
  const response = await fetch(getApiUrl(`/snmp/devices/${id}/status`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch device status");
  return response.json();
}

export async function getSNMPDeviceInterfaces(id: number): Promise<SNMPInterface[]> {
  const response = await fetch(getApiUrl(`/snmp/devices/${id}/interfaces`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch device interfaces");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function getSNMPInterfaceHistory(
  deviceId: number,
  interfaceId: number,
  page = 1,
  limit = 240,
): Promise<PaginatedSNMPInterfaceStats> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const response = await fetch(
    getApiUrl(`/snmp/devices/${deviceId}/interfaces/${interfaceId}/history?${params}`),
  );
  if (!response.ok) return parseErrorOr(response, "Failed to fetch interface history");
  return response.json();
}

export async function getSNMPDeviceMacTable(id: number): Promise<SNMPMacEntry[]> {
  const response = await fetch(getApiUrl(`/snmp/devices/${id}/mac-table`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch mac table");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}
